<template>
  <div class="gallery-pack-tooltip-content">
    <div class="gallery-pack-tooltip-content-head flexing">
      <p class="text-texts-standard-default text-30 font-semibold">
        {{ title }}
      </p>
    </div>

    <div class="gallery-pack-tooltip-content-value flexing py-8">
      <p class="text-60 text-texts-standard-important font-bold mr-1">{{ quantity }}</p>
      <app-icon icon-name="puzzle-80" />
    </div>

    <div
      v-if="getGuaranteedPieces.length"
      class="gallery-pack-tooltip-content-guaranteed text-28 text-texts-standard-default flex justify-between items-center w-full"
    >
      <p class="guaranteed-title pl-4">{{ $t('gallery.containsMin') }}</p>

      <section class="flex items-center justify-evenly">
        <div v-for="(piece, i) in getGuaranteedPieces" :key="i" class="flexing ml-2">
          <span class="font-bold mr-1">{{ piece.guaranteed }}</span>
          <app-main-icon :icon-size="32" :icon-name="`puzzle-${getRarityString(piece)}`" />
        </div>
      </section>
    </div>
    <p class="drop-rates-title text-28 flexing">{{ $t('gallery.dropRates') }}</p>

    <section class="drop-rates">
      <div
        v-for="(piece, i) in pieces"
        :key="i"
        class="underlined-box text-30 flex items-center justify-between px-2"
      >
        <div class="flexing">
          <app-main-icon :icon-size="32" :icon-name="`puzzle-${getRarityString(piece)}`" />
          <span class="ml-2">{{ $t(`gallery.${getRarityString(piece)}Piece`) }}</span>
        </div>

        <div class="flexing">{{ `${piece.probability }%` }}</div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { PropType } from 'vue'
import type { GalleryPackPiece } from '@/interfaces/gallery/Packs'

export default defineComponent({
  name: 'GalleryPackTooltip',

  props: {
    pieces: {
      type: Array as PropType<GalleryPackPiece[]>,
      required: true,
    },

    title: {
      type: String,
      default: '',
    },

    quantity: {
      type: Number,
      default: 0,
    },
  },

  computed: {
    getGuaranteedPieces(): GalleryPackPiece[] {
      return this.pieces.filter((piece: GalleryPackPiece) => piece.guaranteed > 0)
    }
  },

  methods: {
    getRarityString(piece: GalleryPackPiece): string {
      return piece.games_parameter.split('_')[2]
    }
  }
})
</script>

<style lang="scss" scoped>
.gallery-pack-tooltip-content {
  width: 30.25rem;

  &-head {
    height: 3rem;
    @if $isWsm {
      background: linear-gradient(to left, rgba(15, 40, 57, 0), #1e6c9b, rgba(15, 40, 57, 0));
    }
    @if $isSsm {
      background: linear-gradient(
        to right,
        rgba(179, 82, 162, 0.01),
        rgba(179, 82, 162, 0.7),
        #a83c75,
        #a83c75,
        rgba(179, 82, 162, 0.7),
        rgba(179, 82, 162, 0.01)
      );
    }
  }

  &-guaranteed {
    height: 3.125rem;
    @if $isWsm {
      background-color: #377fb8;
    }
    @if $isSsm {
      background-color: #56627f;
    }
    .guaranteed-title {
      line-height: 1.5rem;
    }
  }

  .drop-rates {
    @if $isWsm {
      background-color: rgba(55, 127, 184, 0.5);
    }
    @if $isSsm {
      background-color: rgba(86, 98, 127, 0.5);
    }

    &-title {
      height: 3.125rem;
    }
  }

  .underlined-box {
    position: relative;
    height: 4.125rem;
    &:after {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0rem;
      width: 100%;
      height: 0.063rem;
      @if $isWsm {
        background: linear-gradient(
          to right,
          rgba(200, 200, 200, 0.2),
          rgba(200, 200, 200, 0.8),
          rgba(200, 200, 200, 1),
          rgba(200, 200, 200, 0.8),
          rgba(200, 200, 200, 0.2)
        );
      }
      @if $isSsm {
        background: linear-gradient(
          to right,
          rgba(200, 200, 200, 0.2),
          rgba(200, 200, 200, 0.8),
          rgba(200, 200, 200, 1),
          rgba(200, 200, 200, 0.8),
          rgba(200, 200, 200, 0.2)
        );
      }
    }
    &:last-child {
      &:after {
        content: '';
        background: none;
        height: 0;
      }
    }
  }
}
</style>
